import Phaser from 'phaser';
import { SCENE_KEYS, debugState, designModeState, VERSION, characterOnlyModeState, screenShakeState } from '@/config/constants';
import { AudioManager } from '@/utils/AudioManager';
import { DemoController } from '@/utils/DemoController';
import { transitionTo, fadeIn } from '@/utils/UIAnimations';
import { COLORS_HEX, FONTS } from '@/ui/theme';
import { createPillButton } from '@/ui/PillButton';
import { PostFXManager } from '@/utils/PostFXManager';
import { CausticsTitleFilter } from '@/pipelines/CausticsTitlePipeline';
import { BackgroundManager } from '@/utils/BackgroundManager';
import { GamepadNav } from '@/utils/GamepadNav';

/**
 * Menu Scene — chunky cartoon homepage matching the reference mock.
 *
 *   ┌─────────────────────────────────────────────────────┐
 *   │                  HELMEPA                            │
 *   │            ╭───────────────╮                        │
 *   │            │   POSEIDON    │                        │
 *   │            │   character   │     ⓗs                 │
 *   │            ╰───────────────╯     ◯ HIGH SCORES      │
 *   │           POSEIDON CHALLENGE     ▭ SETTINGS         │
 *   │                                  · · ·              │
 *   │            ╭──────────────╮                         │
 *   │            │     PLAY     │                         │
 *   │            ╰──────────────╯                         │
 *   └─────────────────────────────────────────────────────┘
 *
 * The right-side stack lives inside the canvas safe area; everything
 * else is centered on the X axis. Background video, light rays, bubbles,
 * and vignette are reused from the previous menu.
 */
export class MenuScene extends Phaser.Scene {
  private selectedStage = 1;
  private devPanelVisible = false;
  // Reveals/hides the dev panel. Assigned in createDevPanel(); driven by both
  // the gear and the * hotkey. No-op until the panel has been built.
  private toggleDevPanel: () => void = () => {};
  private nav?: GamepadNav;
  // Set by GameScene when the menu is re-entered straight out of an attract-
  // mode demo. While true, the idle watch fires after 20s instead of 60s so
  // the attract loop keeps cycling. The first real user input snaps the
  // window back to the normal 60s.
  private fromDemo = false;

  constructor() {
    super({ key: SCENE_KEYS.MENU });
  }

  init(data?: { fromDemo?: boolean }): void {
    this.fromDemo = !!data?.fromDemo;

    // Every visit to the title screen is treated as a new session. Drop the
    // current player identity so the next Play forces NameEntryScene, which
    // mints a fresh players row + player_id. Play Again on GameOverScene
    // skips MenuScene by design, so a real "same kiosk session" stays under
    // one player_id; only an explicit return here (Main Menu button, idle
    // timeout, demo bounce, etc.) breaks the session.
    this.registry.remove('playerName');
    this.registry.remove('playerId');
  }

  create(): void {
    const { width, height } = this.cameras.main;
    const centerX = width / 2;

    (this as any)._transitioning = false;

    // The menu is a clean slate: stop any gameplay scene that may still be
    // lingering. Entering the boss from stage 6 leaves that stage's GameScene
    // (and its UIScene) paused, because StageClearScene starts the boss without
    // stopping them. Those orphaned scenes outlive the fight, and on return to
    // the menu they render on top of it (they sit at a higher scene index),
    // burying the menu and trapping the player on a frozen game frame.
    for (const key of [SCENE_KEYS.GAME, SCENE_KEYS.UI, SCENE_KEYS.BOSS]) {
      if (this.scene.isActive(key) || this.scene.isPaused(key) || this.scene.isSleeping(key)) {
        this.scene.stop(key);
      }
    }

    fadeIn(this, 600);

    PostFXManager.attach(this, 'menu');

    const audioManager = AudioManager.getInstance();
    audioManager.initialize(this);
    audioManager.playMusic('menu');

    // === BACKGROUND VIDEO ===
    // Dual-video crossfade — BackgroundManager runs two instances and
    // dissolves between them 3s before loop end so the seam is invisible.
    const bgManager = new BackgroundManager(this);
    bgManager.createBackground({
      type: 'video',
      video: 'world1 video',  // path → key 'bg_video_world1_video'
      scaleMode: 'cover',
    });
    // Dim overlay on top for text/UI legibility (matches old single-video tone).
    const overlay = this.add.graphics();
    overlay.fillStyle(0x000000, 0.45).fillRect(0, 0, width, height).setDepth(1);
    // Tear down both videos when this scene shuts down.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => bgManager.destroyBackground());

    // === LIGHT RAYS ===
    for (let i = 0; i < 4; i++) {
      const rayX = width * 0.15 + i * width * 0.25;
      const ray = this.add.graphics().setDepth(2).setAlpha(0);
      ray.fillStyle(0xFFFFFF, 1).fillTriangle(rayX - 40, -20, rayX + 40, -20, rayX + 100 + i * 30, height * 0.8);
      this.tweens.add({ targets: ray, alpha: { from: 0, to: 0.04 + i * 0.01 }, duration: 2000, delay: 300 + i * 200, ease: 'Power2' });
      this.tweens.add({ targets: ray, x: { from: -15, to: 15 }, duration: 5000 + i * 1000, yoyo: true, repeat: -1, delay: i * 500, ease: 'Sine.easeInOut' });
    }

    // === BUBBLES ===
    if (!this.textures.exists('menu_particle')) {
      const g = this.add.graphics(); g.fillStyle(0xFFFFFF, 1).fillCircle(4, 4, 4);
      g.generateTexture('menu_particle', 8, 8); g.destroy();
    }
    this.add.particles(0, 0, 'menu_particle', {
      x: { min: 0, max: width }, y: height + 20,
      lifespan: { min: 5000, max: 10000 }, speedY: { min: -50, max: -25 }, speedX: { min: -10, max: 10 },
      scale: { start: 0.2, end: 0.7 }, alpha: { start: 0.2, end: 0 },
      tint: [0x4dd0e1, 0x71D2FF, 0x3399CC, 0x88EEFF], frequency: 120, blendMode: 'ADD',
    }).setDepth(3);

    // === VIGNETTE ===
    const vig = this.add.graphics().setDepth(4);
    vig.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.7, 0.7, 0, 0).fillRect(0, 0, width, 130);
    vig.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0, 0, 0.75, 0.75).fillRect(0, height - 110, width, 110);

    // ═══════════════════════════════════════════════════════════════
    //  HELMEPA LOGO — small, centered, top
    // ═══════════════════════════════════════════════════════════════
    if (this.textures.exists('helmepa_logo')) {
      const helmepa = this.add.image(centerX, 90, 'helmepa_logo')
        .setOrigin(0.5)
        .setDepth(10)
        .setAlpha(0);
      helmepa.setScale(198 / helmepa.width);
      this.time.delayedCall(200, () => {
        this.tweens.add({ targets: helmepa, alpha: 0.95, duration: 600, ease: 'Power2' });
      });
    }

    // ═══════════════════════════════════════════════════════════════
    //  POSEIDON CHARACTER — center stage hero
    // ═══════════════════════════════════════════════════════════════
    let character: Phaser.GameObjects.Image | null = null;
    if (this.textures.exists('menu_poseidon_character')) {
      character = this.add.image(centerX, 410, 'menu_poseidon_character')
        .setOrigin(0.5)
        .setDepth(8)
        .setAlpha(0);
      const targetH = 700;
      character.setScale(targetH / character.height);
      // Gentle idle float
      this.tweens.add({
        targets: character,
        y: character.y - 12,
        duration: 3200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      this.time.delayedCall(300, () => {
        this.tweens.add({ targets: character, alpha: 1, duration: 700, ease: 'Power2' });
      });
    }

    // ═══════════════════════════════════════════════════════════════
    //  TITLE LOGO — POSEIDON CHALLENGE banner, overlaps character feet
    //  Intentional glow: sustained aqua bloom (never goes to 0) + slow
    //  shine sweep that drifts at constant speed, so the lettering reads
    //  as "always lit" rather than flickering.
    // ═══════════════════════════════════════════════════════════════
    if (this.textures.exists('title_logo_v4')) {
      const titleLogo = this.add.image(centerX, 770, 'title_logo_v4')
        .setOrigin(0.5)
        .setDepth(11)
        .setAlpha(0);
      titleLogo.setScale(738 / titleLogo.width);

      // Fade-in
      this.time.delayedCall(500, () => {
        this.tweens.add({ targets: titleLogo, alpha: 1, duration: 600, ease: 'Power2' });
      });

      // Soft idle bob — slow + subtle so it reads as "breathing", not bouncing.
      this.tweens.add({
        targets: titleLogo,
        y: 770 - 5,
        duration: 4200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });

      // Caustics overlay — procedural underwater light pattern lives
      // INSIDE the letterforms (the shader uses the texture alpha as a
      // mask, so nothing bleeds outside the title). Three sin-wave layers
      // drift at different speeds + directions for an ever-changing look.
      try {
        CausticsTitleFilter.attachTo(titleLogo)
          .setIntensity(0.5)
          .setSpeed(1.0)
          .setTint(0.85, 1.0, 1.15);
      } catch (e) {
        console.warn('CausticsTitleFilter attach failed:', e);
      }
    }

    // ═══════════════════════════════════════════════════════════════
    //  PLAY BUTTON — canonical primary CTA
    // ═══════════════════════════════════════════════════════════════
    const playBtn = createPillButton(this, {
      x: centerX,
      y: 970,
      label: 'PLAY',
      variant: 'primary',
      size: 'large',
      // Round-cap 3-slice render: keeps the end caps perfect semicircles. The
      // legacy single-stretch squashed the 2.49:1 SVG into a 3.27:1 box, so the
      // caps came out distorted/squared off on any menu after the first (the
      // first menu happened to look right only because the SVG had not finished
      // decoding yet, so it fell back to the rounded code-drawn pill).
      roundedCaps: true,
      onClick: () => this.startGame(),
    });
    // Halo hugs the code-drawn pill body (the full 360x110 box, centered).
    (playBtn as any)._haloShape = { kind: 'rect', w: 360, h: 110, oy: 0 };
    playBtn.setDepth(12).setAlpha(0);
    this.time.delayedCall(700, () => {
      this.tweens.add({ targets: playBtn, alpha: 1, y: playBtn.y, duration: 500, ease: 'Back.easeOut' });
    });

    // ═══════════════════════════════════════════════════════════════
    //  RIGHT SIDE STACK — HIGH SCORES (yellow bubble) / SETTINGS / CREDITS
    //  HIGH SCORES keeps its iconic yellow bubble. SETTINGS + CREDITS
    //  drop to subtle white text labels — they're secondary nav, not CTAs.
    // ═══════════════════════════════════════════════════════════════
    const rightX = width - 130;
    const sideButtons: Phaser.GameObjects.Container[] = [];

    const hsBtn = this.createCircleButton({
      x: rightX,
      y: 760,
      diameter: 150,
      texture: 'menu_btn_yellow_bubble',
      label: 'HIGH\nSCORES',
      labelSize: 22,
      labelColor: '#000000',
      onClick: () => { this.stopIdleWatch(); transitionTo(this, SCENE_KEYS.LEADERBOARD); },
    });
    hsBtn.setDepth(12).setAlpha(0);
    sideButtons.push(hsBtn);

    const sideW = 200, sideH = 60;
    const makeSubtleLink = (label: string, y: number, onClick: () => void): Phaser.GameObjects.Container => {
      const c = this.add.container(rightX, y);
      const t = this.add.text(0, 0, label, {
        fontFamily: FONTS.display,
        fontSize: '24px',
        color: '#FFFFFF',
      }).setOrigin(0.5).setLetterSpacing(2).setAlpha(0.78);
      c.add(t);
      c.setInteractive(
        new Phaser.Geom.Rectangle(-sideW / 2, -sideH / 2, sideW, sideH),
        Phaser.Geom.Rectangle.Contains,
      );
      (c.input as Phaser.Types.Input.InputConfiguration).cursor = 'pointer';
      c.on('pointerover', () => {
        t.setAlpha(1);
        this.tweens.add({ targets: c, scale: 1.06, duration: 140, ease: 'Back.easeOut' });
      });
      c.on('pointerout', () => {
        t.setAlpha(0.78);
        this.tweens.add({ targets: c, scale: 1, duration: 120, ease: 'Power2.easeIn' });
      });
      c.on('pointerdown', () => {
        AudioManager.getInstance().playSFX(this,
          this.cache.audio.exists('sfx_btn_click') ? 'sfx_btn_click' : 'sfx_btn_click_heavy',
          { volume: 0.45 });
        onClick();
      });
      return c;
    };

    const settingsBtn = makeSubtleLink('SETTINGS', 905, () => {
      this.stopIdleWatch();
      transitionTo(this, SCENE_KEYS.SETTINGS);
    });
    (settingsBtn as any)._haloShape = { kind: 'rect', w: sideW, h: sideH };
    settingsBtn.setDepth(12).setAlpha(0);
    sideButtons.push(settingsBtn);

    const controlsBtn = makeSubtleLink('CONTROLS', 985, () => {
      this.stopIdleWatch();
      transitionTo(this, SCENE_KEYS.CONTROLS);
    });
    (controlsBtn as any)._haloShape = { kind: 'rect', w: sideW, h: sideH };
    controlsBtn.setDepth(12).setAlpha(0);
    sideButtons.push(controlsBtn);

    sideButtons.forEach((btn, i) => {
      this.time.delayedCall(900 + i * 110, () => {
        this.tweens.add({ targets: btn, alpha: 1, duration: 400, ease: 'Power2' });
      });
    });

    // === VERSION (dim, bottom-right) ===
    this.add.text(width - 24, height - 14, VERSION, {
      fontSize: '14px', fontFamily: FONTS.display, color: COLORS_HEX.aquaLight,
      stroke: COLORS_HEX.midnightDepth, strokeThickness: 2,
    }).setOrigin(1, 1).setDepth(20).setAlpha(0.35);

    // === DEV PANEL (debug toggles + stage launcher). ===
    // Built but hidden — no gear shows in the live/kiosk build. Press * to
    // reveal it (and * again, or the gear, to hide it).
    this.createDevPanel();
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (event.key === '*') this.toggleDevPanel();
    });

    // === SELECTION HALO + KEYBOARD NAV ===
    // PLAY first so ENTER on a fresh menu fires PLAY without hunting.
    this.setupSelectionHalo([playBtn, ...sideButtons]);
    this.setupIdleWatch();
    this.input.on('pointermove', () => this.resetIdleTimer());
    this.input.on('pointerdown', () => this.resetIdleTimer());
    this.input.keyboard?.on('keydown', () => this.resetIdleTimer());
  }

  /**
   * Yellow circular bubble — exclusive to the HIGH SCORES menu CTA, the
   * one place that breaks the cyan PLAY-pill silhouette by design.
   */
  private createCircleButton(opts: {
    x: number; y: number;
    diameter: number;
    texture: string;
    label: string;
    labelSize: number;
    labelColor?: string;
    onClick: () => void;
  }): Phaser.GameObjects.Container {
    const container = this.add.container(opts.x, opts.y);
    const visual = this.add.container(0, 0);
    const audio = AudioManager.getInstance();

    const bg = this.add.image(0, 0, opts.texture).setOrigin(0.5);
    if (this.textures.exists(opts.texture)) {
      bg.setDisplaySize(opts.diameter, opts.diameter);
    }

    const label = this.add.text(0, 0, opts.label, {
      fontFamily: FONTS.fancy,
      fontSize: `${opts.labelSize}px`,
      color: opts.labelColor ?? '#3a2a00',
      align: 'center',
      stroke: '#fff5d0',
      strokeThickness: 2,
      lineSpacing: 2,
    } as any).setOrigin(0.5).setLetterSpacing(1);

    visual.add([bg, label]);

    const hoverScale = 1.07;
    const hitDiameter = opts.diameter * hoverScale;
    container.add(visual);
    // Do NOT call setSize before setInteractive — it shifts the container's
    // input origin so the hit area only registers on the upper-left (Phaser
    // container quirk, same as PillButton). With no setSize the circle hit
    // area is interpreted around the container anchor, covering the full bubble.
    container.setInteractive(
      new Phaser.Geom.Circle(0, 0, hitDiameter / 2),
      Phaser.Geom.Circle.Contains,
    );
    (container.input as any).cursor = 'pointer';
    (container as any)._haloShape = { kind: 'circle' as const, w: opts.diameter, h: opts.diameter };
    const stopVisualTweens = () => this.tweens.killTweensOf(visual);

    container.on('pointerover', () => {
      stopVisualTweens();
      this.tweens.add({ targets: visual, scaleX: hoverScale, scaleY: hoverScale, duration: 140, ease: 'Back.easeOut' });
      audio.playSFX(this, this.cache.audio.exists('sfx_btn_hover') ? 'sfx_btn_hover' : 'sfx_btn_hover_wood', { volume: 0.35 });
    });
    container.on('pointerout', () => {
      stopVisualTweens();
      this.tweens.add({ targets: visual, scaleX: 1, scaleY: 1, duration: 120, ease: 'Power2.easeIn' });
    });
    container.on('pointerdown', () => {
      audio.playSFX(this, this.cache.audio.exists('sfx_btn_click_heavy') ? 'sfx_btn_click_heavy' : 'sfx_btn_click', { volume: 0.5 });
      stopVisualTweens();
      this.tweens.add({ targets: visual, scaleX: 0.94, scaleY: 0.94, duration: 70, yoyo: true, ease: 'Power2.easeIn' });
      this.time.delayedCall(110, () => opts.onClick());
    });

    return container;
  }

  // ═══════════════════════════════════════════════════════════════
  //  SELECTION HALO + KEYBOARD NAV
  // ═══════════════════════════════════════════════════════════════
  /**
   * Energy-halo selection indicator. Reads `_haloShape` off each button
   * (set by the button builders) so it adapts between the rectangular
   * cyan buttons and the round HIGH SCORES bubble. Three layers:
   *   1. haloGlow  — outer additive ambient glow, breathes alpha + scale.
   *   2. haloBase  — crisp rim + corner brackets (corners suppressed for circles).
   *   3. haloChase — two comet dots orbiting the perimeter + rare lightning flicker.
   * Mouse hover steals focus from the keyboard selection, so users can
   * freely switch input methods.
   */
  private setupSelectionHalo(buttons: Phaser.GameObjects.Container[]): void {
    const haloGlow  = this.add.graphics().setDepth(11).setAlpha(0).setBlendMode(Phaser.BlendModes.ADD);
    const haloBase  = this.add.graphics().setDepth(13).setAlpha(0);
    const haloChase = this.add.graphics().setDepth(13.5).setAlpha(0).setBlendMode(Phaser.BlendModes.ADD);

    type Shape = { kind: 'rect' | 'circle'; w: number; h: number; oy?: number };
    const state = {
      breath: 0,
      chaseT: 0,
      hw: 0, hh: 0, rr: 22,
      shape: 'rect' as Shape['kind'],
      active: false,
    };

    let idx = 0;
    const focused = (): Phaser.GameObjects.Container | null => buttons[idx] ?? null;

    const drawHalo = () => {
      const btn = focused();
      haloGlow.clear(); haloBase.clear(); haloChase.clear();
      if (!btn) { state.active = false; return; }
      const meta: Shape = (btn as any)._haloShape ?? { kind: 'rect', w: 360, h: 110 };
      state.shape = meta.kind;
      // Halo sits exactly on the visual button edge — no outer overflow.
      const hw = meta.w;
      const hh = meta.h;
      const oy = meta.oy ?? 0;
      const rr = meta.kind === 'circle' ? hw / 2 : hh / 2;
      state.hw = hw; state.hh = hh; state.rr = rr;
      state.active = true;

      // Outer ambient glow — sits AT the rim. Inner soft fill is inset by 2 px
      // so it reads as a glow without blooming past the button edge.
      haloGlow.fillStyle(0x4dd0e1, 0.18);
      haloGlow.fillRoundedRect(-hw / 2, -hh / 2, hw, hh, rr);
      haloGlow.fillStyle(0x71D2FF, 0.28);
      haloGlow.fillRoundedRect(-hw / 2 + 2, -hh / 2 + 2, hw - 4, hh - 4, Math.max(rr - 2, 1));
      haloGlow.setPosition(btn.x, btn.y + oy);

      // Base rim — solid anchor under the comets
      haloBase.lineStyle(2, 0x88EEFF, 0.6);
      haloBase.strokeRoundedRect(-hw / 2, -hh / 2, hw, hh, rr);
      haloBase.lineStyle(1, 0xFFFFFF, 0.45);
      haloBase.strokeRoundedRect(-hw / 2 + 4, -hh / 2 + 4, hw - 8, hh - 8, Math.max(rr - 3, 1));

      haloBase.setPosition(btn.x, btn.y + oy);
      haloChase.setPosition(btn.x, btn.y + oy);
    };

    // Outer breath — alpha-only pulse so the halo's footprint stays glued
    // to the button edge frame-to-frame (no scale lift = no overflow).
    this.tweens.add({
      targets: state, breath: 1, duration: 1600, yoyo: true, repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        haloGlow.setAlpha(0.72 + state.breath * 0.28);
        haloBase.setAlpha(0.9 + state.breath * 0.1);
      },
    });
    // Orbiting comets are driven directly off the scene clock rather than a
    // repeating tween, so the loop has no restart hitch — perfectly smooth,
    // constant-speed rotation. One full lap every CHASE_LAP_MS.
    const CHASE_LAP_MS = 2400;

    /** Trace a point at normalized position t (0..1) around the perimeter. */
    const perimPoint = (t: number): { x: number; y: number } => {
      const hw = state.hw, hh = state.hh, r = state.rr;
      if (state.shape === 'circle') {
        const a = -Math.PI / 2 + t * Math.PI * 2;
        const radius = hw / 2;
        return { x: Math.cos(a) * radius, y: Math.sin(a) * radius };
      }
      const halfW = hw / 2, halfH = hh / 2;
      const segTop = hw - 2 * r;
      const segSide = hh - 2 * r;
      const segArc = (Math.PI * r) / 2;
      // Perimeter = 2 top/bottom edges + 2 side edges + 4 corner arcs. The
      // arc term must be ×4 (was ×2), otherwise the path ends at the
      // bottom-left corner and the comet skips the whole left side.
      const total = (segTop + segSide + 2 * segArc) * 2;
      let p = t * total;
      if (p < segTop) return { x: -halfW + r + p, y: -halfH };
      p -= segTop;
      if (p < segArc) {
        const a = -Math.PI / 2 + (p / segArc) * (Math.PI / 2);
        return { x: halfW - r + Math.cos(a) * r, y: -halfH + r + Math.sin(a) * r };
      }
      p -= segArc;
      if (p < segSide) return { x: halfW, y: -halfH + r + p };
      p -= segSide;
      if (p < segArc) {
        const a = 0 + (p / segArc) * (Math.PI / 2);
        return { x: halfW - r + Math.cos(a) * r, y: halfH - r + Math.sin(a) * r };
      }
      p -= segArc;
      if (p < segTop) return { x: halfW - r - p, y: halfH };
      p -= segTop;
      if (p < segArc) {
        const a = Math.PI / 2 + (p / segArc) * (Math.PI / 2);
        return { x: -halfW + r + Math.cos(a) * r, y: halfH - r + Math.sin(a) * r };
      }
      p -= segArc;
      if (p < segSide) return { x: -halfW, y: halfH - r - p };
      p -= segSide;
      const a = Math.PI + (p / segArc) * (Math.PI / 2);
      return { x: -halfW + r + Math.cos(a) * r, y: -halfH + r + Math.sin(a) * r };
    };

    // Per-frame chase redraw — two soft light streaks flowing around the rim.
    // The tail is drawn as many tightly-spaced, overlapping dots with an eased
    // fade so it reads as one continuous comet of light rather than discrete
    // pips. No random flicker — the motion stays smooth and predictable.
    this.events.on(Phaser.Scenes.Events.UPDATE, (time: number) => {
      if (!state.active) return;
      state.chaseT = (time / CHASE_LAP_MS) % 1;
      haloChase.clear();
      for (let k = 0; k < 2; k++) {
        const tBase = (state.chaseT + k * 0.5) % 1;
        const trailSteps = 22;
        for (let i = trailSteps - 1; i >= 0; i--) {
          const tt = (tBase - i * 0.009 + 1) % 1;
          const pt = perimPoint(tt);
          const fade = 1 - i / trailSteps;     // 1 at the head → 0 at the tail
          const soft = fade * fade;            // ease the tail so it melts out
          const radius = 1.5 + 5 * fade;
          haloChase.fillStyle(i === 0 ? 0xFFFFFF : 0x88EEFF, 0.85 * soft);
          haloChase.fillCircle(pt.x, pt.y, radius);
          if (i === 0) {
            haloChase.fillStyle(0x71D2FF, 0.35);
            haloChase.fillCircle(pt.x, pt.y, 14);
            haloChase.fillStyle(0x4dd0e1, 0.16);
            haloChase.fillCircle(pt.x, pt.y, 22);
          }
        }
      }
    });

    const moveTo = (i: number) => {
      idx = (i + buttons.length) % buttons.length;
      drawHalo();
    };

    // Mouse hover steals focus
    buttons.forEach((btn, i) => {
      btn.on('pointerover', () => {
        if (idx !== i) { idx = i; drawHalo(); }
      });
    });

    const navUp    = () => moveTo(idx === 0 ? buttons.length - 1 : idx - 1);
    const navDown  = () => moveTo(idx === buttons.length - 1 ? 0 : idx + 1);
    const navLeft  = () => moveTo(0);
    const navRight = () => moveTo(idx === 0 ? 1 : idx);
    const fire     = () => { this.resetIdleTimer(); focused()?.emit('pointerdown'); };

    const kb = this.input.keyboard;
    if (kb) {
      // PLAY (0) is alone; the right column is HS(1), SETTINGS(2), CONTROLS(3).
      kb.on('keydown-UP',    navUp);
      kb.on('keydown-DOWN',  navDown);
      kb.on('keydown-LEFT',  navLeft);
      kb.on('keydown-RIGHT', navRight);
      kb.on('keydown-SPACE', fire);
      kb.on('keydown-ENTER', fire);
    }

    // Gamepad: D-pad / left stick navigate; A confirms; movement also resets idle.
    this.nav = new GamepadNav(this, {
      onUp:    () => { this.resetIdleTimer(); navUp(); },
      onDown:  () => { this.resetIdleTimer(); navDown(); },
      onLeft:  () => { this.resetIdleTimer(); navLeft(); },
      onRight: () => { this.resetIdleTimer(); navRight(); },
      onConfirm: fire,
    });

    // Reveal halo after the button entrance tweens settle.
    this.time.delayedCall(1100, () => {
      drawHalo();
      this.tweens.add({ targets: [haloGlow, haloBase, haloChase], alpha: 1, duration: 320, ease: 'Power2' });
    });
  }

  update(): void {
    this.nav?.update();
  }

  private setupIdleWatch(): void {
    // When we just bounced back from an attract demo, the user explicitly
    // asked for a 20s breather on the menu before the next stage kicks off.
    // From a cold menu entry, keep the standard 60s idle window.
    const timeoutMs = this.fromDemo ? 20000 : 60000;
    DemoController.getInstance().startIdleWatch(() => {
      this.scene.start(SCENE_KEYS.GAME, { stage: 1 + Math.floor(Math.random() * 6), score: 0, demoMode: true });
    }, timeoutMs);
  }
  private resetIdleTimer(): void {
    // Any real input means a human just took the wheel; restore the normal
    // 60s window so they have room to navigate. The fromDemo flag is also
    // cleared so a subsequent setupIdleWatch call (e.g. after stopIdleWatch
    // for a sub-scene) does not bring back the 20s window.
    if (this.fromDemo) this.fromDemo = false;
    DemoController.getInstance().resetIdleTimer(60000);
  }
  private stopIdleWatch(): void { DemoController.getInstance().stopIdleWatch(); }

  // ═══════════════════════════════════════════════════════════════
  //  DEV PANEL — gear in top-left, debug toggles + stage launcher
  // ═══════════════════════════════════════════════════════════════
  private createDevPanel(): void {
    const px = 20, sy = 100;
    const gear = this.add.text(15, 86, '\u2699', { fontSize: '28px' }).setDepth(20).setAlpha(0.4).setVisible(false);
    gear.on('pointerover', () => gear.setAlpha(1));
    gear.on('pointerout', () => { if (!this.devPanelVisible) gear.setAlpha(0.4); });
    const panelBg = this.add.rectangle(px - 5, sy - 15, 380, 260, 0x000000, 0.75).setOrigin(0, 0).setDepth(19).setVisible(false);
    const cbs = [
      { y: sy, label: 'Debug Mode', color: 0x4dd0e1, tc: '#9ee4f2', get: () => debugState.getDebugMode(), set: (v: boolean) => debugState.setDebugMode(v) },
      { y: sy + 38, label: 'Design Mode (No Damage)', color: 0xFFD700, tc: '#FFD700', get: () => designModeState.isDesignMode(), set: (v: boolean) => designModeState.setDesignMode(v) },
      { y: sy + 76, label: 'Character Only', color: 0xFF69B4, tc: '#FF69B4', get: () => characterOnlyModeState.isCharacterOnlyMode(), set: (v: boolean) => characterOnlyModeState.setCharacterOnlyMode(v) },
      { y: sy + 114, label: 'Screen Shake', color: 0x66FF66, tc: '#66FF66', get: () => { screenShakeState.loadFromStorage(); return screenShakeState.isEnabled(); }, set: (v: boolean) => screenShakeState.setEnabled(v) },
    ];
    const els: Phaser.GameObjects.GameObject[] = [panelBg];
    cbs.forEach(cb => {
      const bg = this.add.rectangle(px, cb.y, 22, 22, 0x1a3a52).setOrigin(0, 0.5).setStrokeStyle(2, cb.color).setDepth(20).setVisible(false).setInteractive({ useHandCursor: true });
      const mk = this.add.rectangle(px + 11, cb.y, 14, 14, cb.color).setOrigin(0.5).setDepth(20).setVisible(false).setAlpha(cb.get() ? 1 : 0);
      const lb = this.add.text(px + 32, cb.y, cb.label, { fontSize: '18px', fontFamily: FONTS.display, color: cb.tc }).setOrigin(0, 0.5).setDepth(20).setVisible(false);
      bg.on('pointerdown', () => { const v = !cb.get(); cb.set(v); mk.setAlpha(v ? 1 : 0); });
      els.push(bg, mk, lb);
    });
    const ssY = sy + 160;
    const sn: Record<number, string> = { 1: '1: Calm Waters', 2: '2: Rising Tides', 3: '3: Golden Hour', 4: '4: Sunset Siege', 5: '5: Deck Battle', 6: "6: Poseidon's Trial", 7: '7: BOSS - Hydra' };
    const sl = this.add.text(px, ssY, 'Stage:', { fontSize: '18px', fontFamily: FONTS.display, color: '#9ee4f2' }).setOrigin(0, 0.5).setDepth(20).setVisible(false);
    const st = this.add.text(px + 100, ssY, sn[this.selectedStage], { fontSize: '16px', fontFamily: FONTS.display, color: '#FFF' }).setOrigin(0, 0.5).setDepth(20).setVisible(false);
    const sL = this.add.text(px + 65, ssY, '\u25C0', { fontSize: '18px', color: '#4dd0e1' }).setOrigin(0.5).setDepth(20).setVisible(false).setInteractive({ useHandCursor: true });
    sL.on('pointerdown', () => { this.selectedStage = Math.max(1, this.selectedStage - 1); st.setText(sn[this.selectedStage]); });
    const sR = this.add.text(px + 275, ssY, '\u25B6', { fontSize: '18px', color: '#4dd0e1' }).setOrigin(0.5).setDepth(20).setVisible(false).setInteractive({ useHandCursor: true });
    sR.on('pointerdown', () => { this.selectedStage = Math.min(7, this.selectedStage + 1); st.setText(sn[this.selectedStage]); });
    const lb = this.add.text(px, ssY + 30, '\u25B6 LAUNCH STAGE', { fontSize: '16px', fontFamily: FONTS.display, color: '#44FF44' }).setOrigin(0, 0.5).setDepth(20).setVisible(false).setInteractive({ useHandCursor: true });
    lb.on('pointerover', () => lb.setColor('#88FF88')); lb.on('pointerout', () => lb.setColor('#44FF44'));
    lb.on('pointerdown', () => { this.stopIdleWatch(); this.registry.set('playerName', this.registry.get('playerName') || 'DEV'); this.registry.set('playerId', this.registry.get('playerId') || 'dev'); if (this.selectedStage === 7) this.scene.start(SCENE_KEYS.BOSS, { score: 0 }); else this.scene.start(SCENE_KEYS.GAME, { stage: this.selectedStage, score: 0 }); });
    els.push(lb);
    const ab = this.add.text(px + 180, ssY + 30, '\uD83E\uDD16 AUTO-PLAY', { fontSize: '16px', fontFamily: FONTS.display, color: '#00CCFF' }).setOrigin(0, 0.5).setDepth(20).setVisible(false).setInteractive({ useHandCursor: true });
    ab.on('pointerover', () => ab.setColor('#66EEFF')); ab.on('pointerout', () => ab.setColor('#00CCFF'));
    ab.on('pointerdown', () => { this.stopIdleWatch(); this.registry.set('playerName', this.registry.get('playerName') || 'AUTO'); this.registry.set('playerId', this.registry.get('playerId') || 'auto'); if (this.selectedStage === 7) this.scene.start(SCENE_KEYS.BOSS, { score: 0, autoPlay: true }); else this.scene.start(SCENE_KEYS.GAME, { stage: this.selectedStage, score: 0, autoPlay: true }); });
    els.push(ab, sl, st, sL, sR);
    this.toggleDevPanel = () => {
      this.devPanelVisible = !this.devPanelVisible;
      gear.setVisible(this.devPanelVisible).setAlpha(this.devPanelVisible ? 1 : 0.4);
      if (this.devPanelVisible) gear.setInteractive({ useHandCursor: true }); else gear.disableInteractive();
      els.forEach(e => (e as any).setVisible(this.devPanelVisible));
      cbs.forEach((cb, i) => { (els[1 + i * 3 + 1] as any).setAlpha(cb.get() ? 1 : 0); });
    };
    gear.on('pointerdown', () => this.toggleDevPanel());
  }

  // ═══════════════════════════════════════════════════════════════
  //  NAVIGATION
  // ═══════════════════════════════════════════════════════════════
  private startGame(): void {
    this.stopIdleWatch();
    const playerName = this.registry.get('playerName');
    if (!playerName) {
      transitionTo(this, SCENE_KEYS.NAME_ENTRY, { stage: 1 }, 500, 0x000000, 'parallaxPush');
    } else {
      transitionTo(this, SCENE_KEYS.GAME, { stage: 1, score: 0 }, 500, 0x000000, 'parallaxPush');
    }
  }
}
